import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AmortizeLeases,
  AmortizeLeasesResult,
  CreateLease,
  Lease,
  LeaseListRow,
  LeaseSchedule,
  LeaseStatus,
  LeaseWithSchedule,
  ModificationPreview,
  ModificationResult,
  ModifyLease,
  PreviewModification,
  PreviewTermination,
  TerminateLease,
  TerminationPreview,
  TerminationResult,
} from '@dcc-erp/shared';

import { api } from '../api';

// Cache scopes keyed by companyId so DCC/TTOP/RCI switches get independent
// caches (same pattern as ar.ts / loans.ts).
const leasesKey = (companyId: string | undefined) => ['leases', companyId] as const;

export interface LeaseFilters {
  status?: LeaseStatus;
  lessorVendorId?: string;
}

export function useLeases(companyId: string | undefined, filters: LeaseFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.lessorVendorId) qs.set('lessorVendorId', filters.lessorVendorId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...leasesKey(companyId), filters],
    queryFn: () =>
      api<LeaseListRow[]>(`/companies/${companyId}/leases${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useLease(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['leases', companyId, id],
    queryFn: () => api<LeaseWithSchedule>(`/companies/${companyId}/leases/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useLeaseSchedule(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['leases', companyId, id, 'schedule'],
    queryFn: () => api<LeaseSchedule[]>(`/companies/${companyId}/leases/${id}/schedule`),
    enabled: !!companyId && !!id,
  });
}

// ============================================================
// Tax reconciliation — per-fiscal-year book (interest + ROU dep) vs tax
// (rental paid) summary + DTA balance + lifetime totals. Used by the
// "เปรียบเทียบภาษี" tab on /lease.
// ============================================================

export interface LeaseTaxYearRow {
  fiscalYear: number;
  periodCount: number;
  bookExpense: string;
  taxExpense: string;
  diff: string;
  cumulativeDiff: string;
  dtaBalance: string;
}

export interface LeaseTaxReconciliation {
  leaseId: string;
  voucherNo: string;
  taxAnnualRate: string;
  currency: 'THB';
  years: LeaseTaxYearRow[];
  lifetime: {
    bookExpense: string;
    taxExpense: string;
    residual: string;
    peakDtaBalance: string;
    peakDtaYear: number | null;
  };
}

export function useLeaseTaxReconciliation(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['leases', companyId, id, 'tax-reconciliation'],
    queryFn: () =>
      api<LeaseTaxReconciliation>(`/companies/${companyId}/leases/${id}/tax-reconciliation`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateLease(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: CreateLease; idempotencyKey?: string }) =>
      api<Lease>(`/companies/${companyId}/leases`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leasesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// ============================================================
// Early termination (Phase 16) — preview (no posting) + commit.
// ============================================================

// Preview is a POST that returns the computed catch-up + derecognition +
// gain/loss without posting. Modelled as a query keyed on the inputs so it
// re-runs when the user changes the date / penalty in the modal.
export function useTerminationPreview(
  companyId: string | undefined,
  id: string | undefined,
  data: PreviewTermination,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['leases', companyId, id, 'terminate-preview', data] as const,
    queryFn: () =>
      api<TerminationPreview>(`/companies/${companyId}/leases/${id}/terminate/preview`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    enabled: enabled && !!companyId && !!id,
    // Inputs are part of the key; treat each combination as fresh.
    staleTime: 0,
    retry: false,
  });
}

export function useTerminateLease(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TerminateLease }) =>
      api<TerminationResult>(`/companies/${companyId}/leases/${id}/terminate`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leasesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// ============================================================
// Mid-term modification (Phase 17) — preview (no posting) + commit.
// ============================================================

export function useModificationPreview(
  companyId: string | undefined,
  id: string | undefined,
  data: PreviewModification,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['leases', companyId, id, 'modify-preview', data] as const,
    queryFn: () =>
      api<ModificationPreview>(`/companies/${companyId}/leases/${id}/modify/preview`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    enabled: enabled && !!companyId && !!id,
    staleTime: 0,
    retry: false,
  });
}

export function useModifyLease(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ModifyLease }) =>
      api<ModificationResult>(`/companies/${companyId}/leases/${id}/modify`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leasesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useAmortizeLeases(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: AmortizeLeases; idempotencyKey?: string }) =>
      api<AmortizeLeasesResult>(`/companies/${companyId}/leases/amortize`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leasesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}
